import React, { useState } from 'react';
import { WorkOrder } from '../../types/workOrder';
import { ActivityEntry, CreateActivityDto } from '../../types/activity';
import { CreateInspectionDto, CreateVerificationDto } from '../../types/inspection';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ActivityTimeline } from '../../components/activity/ActivityTimeline';
import { CommentForm } from '../../components/activity/CommentForm';
import { AttachmentManager } from '../../components/attachment/AttachmentManager';
import { SLABadge } from '../../components/sla/SLABadge';
import { InspectionForm } from '../../components/inspection/InspectionForm';
import { DepartmentConfirmationForm } from '../../components/inspection/DepartmentConfirmationForm';
import { ClosureSummaryCard } from '../../components/inspection/ClosureSummaryCard';
import { InspectionHistoryTimeline } from '../../components/inspection/InspectionHistoryTimeline';
import { InspectionResultBadge } from '../../components/inspection/InspectionResultBadge';
import { CostEntryForm } from '../../components/billing/CostEntryForm';
import { QuotationForm } from '../../components/billing/QuotationForm';
import { QuotationApprovalView } from '../../components/billing/QuotationApprovalView';
import { BillCard } from '../../components/billing/BillCard';
import { useInspection } from '../../hooks/useInspection';
import { useCostEntry } from '../../hooks/useCostEntry';
import { workOrderService } from '../../services/workOrder/workOrderService';
import { ArrowLeft, Clock, Wrench, CheckCircle2, MessageSquare, Paperclip, ClipboardCheck, History, IndianRupee, Plus } from 'lucide-react';

interface WorkOrderDetailPageProps {
  workOrder: WorkOrder;
  onBack: () => void;
}

const DEMO_WO_ACTIVITIES: ActivityEntry[] = [
  {
    id: 'WO-ACT-001',
    entity_type: 'work_order',
    entity_id: 'WO-2026-00001',
    activity_type: 'status_change',
    author_id: 'USR-00001',
    author_name: 'System',
    author_role: 'ADMIN',
    content: 'Work order created and assigned to vendor',
    mentions: [],
    attachments: [],
    is_internal: false,
    created_at: '2026-09-10T09:00:00Z',
    system_meta: { field: 'status', old_value: '', new_value: 'Assigned' },
  },
  {
    id: 'WO-ACT-002',
    entity_type: 'work_order',
    entity_id: 'WO-2026-00001',
    activity_type: 'comment',
    author_id: 'USR-00005',
    author_name: 'CoolTech Services',
    author_role: 'VENDOR',
    content: 'Technician Ahmad Khan assigned. En route to site. ETA 30 minutes.',
    mentions: [],
    attachments: [],
    is_internal: false,
    created_at: '2026-09-10T09:15:00Z',
  },
  {
    id: 'WO-ACT-003',
    entity_type: 'work_order',
    entity_id: 'WO-2026-00001',
    activity_type: 'comment',
    author_id: 'USR-00006',
    author_name: 'Ahmad Khan',
    author_role: 'TECHNICIAN',
    content: 'Arrived on site. Inspecting AC unit. Compressor E4 fault confirmed.',
    mentions: [],
    attachments: [{ id: 'WO-ATT-001', file_name: 'fault_photo.jpg', file_type: 'image/jpeg', data_url: '', uploaded_at: '2026-09-10T10:00:00Z' }],
    is_internal: false,
    created_at: '2026-09-10T10:00:00Z',
  },
];

const AVAILABLE_USERS = [
  { user_id: 'USR-00003', name: 'Rajesh Kumar' },
  { user_id: 'USR-00004', name: 'Suresh' },
  { user_id: 'USR-00005', name: 'CoolTech Services' },
  { user_id: 'USR-00006', name: 'Ahmad Khan' },
];

type DetailView = 'details' | 'activity' | 'attachments' | 'billing' | 'history';

export const WorkOrderDetailPage: React.FC<WorkOrderDetailPageProps> = ({ workOrder, onBack }) => {
  const [wo, setWo] = useState<WorkOrder>(workOrder);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailView>('details');
  const [activities, setActivities] = useState<ActivityEntry[]>(DEMO_WO_ACTIVITIES);
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [showConfirmationForm, setShowConfirmationForm] = useState(false);

  const {
    latestInspection,
    latestVerification,
    closureHistory,
    submitInspection,
    submitVerification,
    closeComplaint,
    refresh: refreshInspection,
  } = useInspection(wo.work_order_id, wo.complaint_id);

  const {
    cost,
    quotations,
    bills,
    saveCost,
    submitQuotation,
    approveQuotation,
    rejectQuotation,
    generateBill,
    markBillPaid,
    refresh: refreshBilling,
  } = useCostEntry(wo.work_order_id, wo.complaint_id, wo.vendor_id);

  const [showCostForm, setShowCostForm] = useState(false);
  const [showQuotationForm, setShowQuotationForm] = useState(false);

  const handleUpdateStatus = async (newStatus: WorkOrder['status']) => {
    setIsUpdating(true);
    const res = await workOrderService.updateStatus(wo.work_order_id, newStatus);
    if (res.success && res.data) {
      setWo(res.data);
      const statusEntry: ActivityEntry = {
        id: `WO-ACT-${Date.now()}`,
        entity_type: 'work_order',
        entity_id: wo.work_order_id,
        activity_type: 'status_change',
        author_id: 'USR-00001',
        author_name: 'Current User',
        author_role: 'ADMIN',
        content: `Status changed to ${newStatus}`,
        mentions: [],
        attachments: [],
        is_internal: false,
        created_at: new Date().toISOString(),
        system_meta: { field: 'status', old_value: wo.status, new_value: newStatus },
      };
      setActivities(prev => [...prev, statusEntry]);
    }
    setIsUpdating(false);
  };

  const handleInspectionSubmit = async (data: CreateInspectionDto) => {
    setIsUpdating(true);
    const res = await submitInspection(data);
    if (res.success) {
      const newStatus = data.result === 'pass' ? 'Inspection Passed' : data.result === 'fail' ? 'Inspection Failed' : 'Under Inspection';
      await workOrderService.updateStatus(wo.work_order_id, newStatus as any);
      setWo(prev => ({ ...prev, status: newStatus as any }));
      setShowInspectionForm(false);

      const inspectionEntry: ActivityEntry = {
        id: `WO-ACT-${Date.now()}`,
        entity_type: 'work_order',
        entity_id: wo.work_order_id,
        activity_type: 'inspection',
        author_id: 'USR-00004',
        author_name: 'Suresh',
        author_role: 'SUPERVISOR',
        content: `Inspection completed â€” ${data.result.charAt(0).toUpperCase() + data.result.slice(1)}`,
        mentions: [],
        attachments: [],
        is_internal: false,
        created_at: new Date().toISOString(),
      };
      setActivities(prev => [...prev, inspectionEntry]);
    }
    setIsUpdating(false);
  };

  const handleVerificationSubmit = async (data: CreateVerificationDto) => {
    setIsUpdating(true);
    const res = await submitVerification(data);
    if (res.success) {
      if (data.is_satisfactory) {
        await workOrderService.updateStatus(wo.work_order_id, 'Inspected' as any);
        setWo(prev => ({ ...prev, status: 'Inspected' as any }));
      }
      setShowConfirmationForm(false);

      const verificationEntry: ActivityEntry = {
        id: `WO-ACT-${Date.now()}`,
        entity_type: 'work_order',
        entity_id: wo.work_order_id,
        activity_type: 'verification',
        author_id: 'USR-00007',
        author_name: 'Dept User',
        author_role: 'DEPARTMENT_USER',
        content: `Department verification â€” ${data.is_satisfactory ? 'Satisfactory' : 'Unsatisfactory'} (${data.rating}/5)`,
        mentions: [],
        attachments: [],
        is_internal: false,
        created_at: new Date().toISOString(),
      };
      setActivities(prev => [...prev, verificationEntry]);

      if (data.is_satisfactory) {
        const closureRes = await closeComplaint(res.data.verification_id, data.rating);
        if (closureRes.success) {
          const closureEntry: ActivityEntry = {
            id: `WO-ACT-${Date.now()}`,
            entity_type: 'work_order',
            entity_id: wo.work_order_id,
            activity_type: 'closure',
            author_id: 'USR-00007',
            author_name: 'Dept User',
            author_role: 'DEPARTMENT_USER',
            content: 'Complaint formally closed',
            mentions: [],
            attachments: [],
            is_internal: false,
            created_at: new Date().toISOString(),
          };
          setActivities(prev => [...prev, closureEntry]);
        }
      }
    }
    setIsUpdating(false);
  };

  const handleAddActivity = (dto: CreateActivityDto) => {
    const newEntry: ActivityEntry = {
      id: `WO-ACT-${Date.now()}`,
      entity_type: dto.entity_type,
      entity_id: dto.entity_id,
      activity_type: dto.activity_type,
      author_id: 'USR-00001',
      author_name: 'Current User',
      author_role: 'ADMIN',
      content: dto.content,
      mentions: dto.mentions,
      attachments: dto.attachments,
      is_internal: dto.is_internal,
      created_at: new Date().toISOString(),
    };
    setActivities(prev => [...prev, newEntry]);
  };

  const commentCount = activities.filter(a => a.activity_type === 'comment' || a.activity_type === 'internal_note').length;

  const isClosed = wo.status === 'Inspected' || wo.status === 'Inspection Passed';
  const canInspect = wo.status === 'Completed';
  const canVerify = wo.status === 'Inspection Passed' && latestInspection && !latestVerification;

  const inspectionSummary = latestInspection
    ? `Inspection ${latestInspection.result.toUpperCase()} by ${latestInspection.inspector_name} on ${new Date(latestInspection.inspection_date).toLocaleDateString()}. Notes: ${latestInspection.notes}`
    : '';

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between border-b border-slate-800 pb-4">
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeft className="w-4 h-4" />}>
          Back to Work Orders
        </Button>
        <div className="flex items-center gap-2">
          <Badge label={wo.status} variant={isClosed ? 'success' : wo.status === 'Inspection Failed' ? 'danger' : 'info'} />
          {wo.status !== 'Completed' && wo.status !== 'Inspected' && (
            <SLABadge createdAt={wo.created_at} priorityName="Medium" completedAt={wo.completion_date} />
          )}
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-sky-400 font-semibold">{wo.work_order_id}</span>
              <span className="text-xs text-slate-500 font-mono">Linked Complaint: {wo.complaint_id}</span>
            </div>
            <h1 className="text-xl font-semibold text-slate-100 mt-1">{wo.description}</h1>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500">Estimated Cost</p>
            <p className="text-2xl font-semibold text-emerald-400">${wo.estimated_cost}</p>
          </div>
        </div>

        <div className="p-4 bg-slate-950 rounded-lg border border-slate-800 space-y-1">
          <p className="text-xs font-semibold text-sky-400">Technical Scope of Work</p>
          <p className="text-xs text-slate-300 leading-relaxed">{wo.scope_of_work}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs pt-2">
          <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 flex items-center gap-3">
            <Wrench className="w-5 h-5 text-sky-400" />
            <div>
              <p className="text-[10px] text-slate-500 font-medium">Assigned Vendor</p>
              <p className="font-semibold text-slate-200">{wo.vendor_name || 'Unassigned'}</p>
            </div>
          </div>
          <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-[10px] text-slate-500 font-medium">Target Date</p>
              <p className="font-semibold text-slate-200">{wo.target_date}</p>
            </div>
          </div>
          <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <div>
              <p className="text-[10px] text-slate-500 font-medium">Supervising Officer</p>
              <p className="font-semibold text-slate-200">{wo.supervisor_name || 'Supervisor Suresh'}</p>
            </div>
          </div>
        </div>

        {latestInspection && (
          <div className="flex items-center gap-3 p-3 bg-slate-950 rounded-lg border border-slate-800">
            <ClipboardCheck className="w-5 h-5 text-sky-400" />
            <div className="flex-1">
              <p className="text-[10px] text-slate-500 font-medium">Last Inspection</p>
              <div className="flex items-center gap-2">
                <InspectionResultBadge result={latestInspection.result} />
                <span className="text-xs text-slate-400">by {latestInspection.inspector_name}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-800 justify-end">
          {wo.status === 'Assigned' && (
            <Button variant="primary" isLoading={isUpdating} onClick={() => handleUpdateStatus('In Progress')}>
              Start Work Execution
            </Button>
          )}
          {wo.status === 'In Progress' && (
            <Button variant="primary" isLoading={isUpdating} onClick={() => handleUpdateStatus('Completed')}>
              Mark Work Completed
            </Button>
          )}
          {canInspect && !showInspectionForm && (
            <Button variant="outline" onClick={() => { setShowInspectionForm(true); setShowConfirmationForm(false); }} icon={<ClipboardCheck className="w-4 h-4" />}>
              Conduct Supervisor Inspection
            </Button>
          )}
          {canVerify && !showConfirmationForm && (
            <Button variant="primary" onClick={() => { setShowConfirmationForm(true); setShowInspectionForm(false); }} icon={<CheckCircle2 className="w-4 h-4" />}>
              Confirm & Rate Work
            </Button>
          )}
        </div>
      </div>

      {showInspectionForm && (
        <Card title="Supervisor Inspection" subtitle="Complete the inspection form below">
          <InspectionForm
            workOrderId={wo.work_order_id}
            complaintId={wo.complaint_id}
            onSubmit={handleInspectionSubmit}
            onCancel={() => setShowInspectionForm(false)}
          />
        </Card>
      )}

      {showConfirmationForm && latestInspection && (
        <Card title="Department Confirmation" subtitle="Verify work quality and provide feedback">
          <DepartmentConfirmationForm
            workOrderId={wo.work_order_id}
            complaintId={wo.complaint_id}
            inspectionId={latestInspection.inspection_id}
            inspectionSummary={inspectionSummary}
            onSubmit={handleVerificationSubmit}
            onCancel={() => setShowConfirmationForm(false)}
          />
        </Card>
      )}

      {isClosed && latestVerification && (
        <ClosureSummaryCard
          closure={{
            closure_id: `CLS-2026-00001`,
            complaint_id: wo.complaint_id,
            work_order_id: wo.work_order_id,
            verification_id: latestVerification.verification_id,
            closed_by_user_id: 'USR-00007',
            closed_by_user_name: latestVerification.verifier_name,
            closed_at: latestVerification.verification_date,
            final_status: 'closed',
            archive_eligible: true,
            total_duration_hours: 72,
            satisfaction_rating: latestVerification.rating,
          }}
        />
      )}

      <div className="flex border-b border-slate-800 gap-2">
        <button
          onClick={() => setActiveTab('details')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-lg flex items-center gap-2 ${
            activeTab === 'details' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400'
          }`}
        >
          <Wrench className="w-4 h-4" /> Details
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-lg flex items-center gap-2 ${
            activeTab === 'activity' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400'
          }`}
        >
          <MessageSquare className="w-4 h-4" /> Activity & Discussion ({commentCount})
        </button>
        <button
          onClick={() => setActiveTab('attachments')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-lg flex items-center gap-2 ${
            activeTab === 'attachments' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400'
          }`}
        >
          <Paperclip className="w-4 h-4" /> Attachments
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-lg flex items-center gap-2 ${
            activeTab === 'history' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400'
          }`}
        >
          <History className="w-4 h-4" /> Inspection History
        </button>
        <button
          onClick={() => setActiveTab('billing')}
          className={`px-4 py-2 text-xs font-semibold rounded-t-lg flex items-center gap-2 ${
            activeTab === 'billing' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400'
          }`}
        >
          <IndianRupee className="w-4 h-4" /> Cost & Billing
        </button>
      </div>

      {activeTab === 'activity' && (
        <Card title="Work Order Activity Timeline" subtitle="Comments, updates, and status changes">
          <ActivityTimeline entries={activities} currentUserRole="ADMIN" />
          <div className="mt-4 pt-4 border-t border-slate-800">
            <CommentForm
              entityType="work_order"
              entityId={wo.work_order_id}
              currentUser={{ user_id: 'USR-00001', name: 'Current User', role: 'ADMIN' }}
              availableUsers={AVAILABLE_USERS}
              onSubmit={handleAddActivity}
            />
          </div>
        </Card>
      )}

      {activeTab === 'attachments' && (
        <Card title="Work Order Attachments" subtitle="Upload, preview, and manage files for this work order">
          <AttachmentManager entityType="work_order" entityId={wo.work_order_id} />
        </Card>
      )}

      {activeTab === 'history' && (
        <Card title="Inspection & Closure History" subtitle="Timeline of all inspection, verification, and closure events">
          <InspectionHistoryTimeline events={closureHistory} />
        </Card>
      )}

      {activeTab === 'billing' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-200">Cost & Billing Management</h3>
            <div className="flex gap-2">
              {!showCostForm && !showQuotationForm && (
                <>
                  <Button variant="outline" size="sm" onClick={() => { setShowCostForm(true); setShowQuotationForm(false); }} icon={<Plus className="w-3.5 h-3.5" />}>
                    {cost ? 'Edit Cost Entry' : 'Add Cost Entry'}
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => { setShowQuotationForm(true); setShowCostForm(false); }} icon={<Plus className="w-3.5 h-3.5" />}>
                    Submit Quotation
                  </Button>
                </>
              )}
            </div>
          </div>

          {showCostForm && (
            <Card title="Work Order Cost Entry" subtitle="Add line items for materials, labor, service, and other charges">
              <CostEntryForm
                workOrderId={wo.work_order_id}
                complaintId={wo.complaint_id}
                vendorId={wo.vendor_id || 'VND-00000'}
                initialCost={cost ? {
                  materials: cost.materials,
                  labor: cost.labor,
                  service: cost.service,
                  other: cost.other,
                  tax_rate: cost.tax_rate,
                } : undefined}
                onSubmit={async (materials, labor, service, other, taxRate) => {
                  await saveCost(materials, labor, service, other, taxRate);
                  setShowCostForm(false);
                  refreshBilling();
                }}
                onCancel={() => setShowCostForm(false)}
                isSaving={false}
              />
            </Card>
          )}

          {showQuotationForm && (
            <QuotationForm
              workOrderId={wo.work_order_id}
              vendorId={wo.vendor_id || 'VND-00000'}
              onSubmit={async (items, validityDays, notes) => {
                await submitQuotation(items, validityDays, notes);
                setShowQuotationForm(false);
                refreshBilling();
              }}
              onCancel={() => setShowQuotationForm(false)}
            />
          )}

          {!showCostForm && !showQuotationForm && (
            <>
              {cost && (
                <Card title="Cost Entry" subtitle={`Status: ${cost.status.toUpperCase()}`}>
                  <div className="space-y-2">
                    {cost.materials.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-400 mb-1">Materials</p>
                        {cost.materials.map(item => (
                          <div key={item.item_id} className="flex justify-between text-xs py-1 px-2 bg-slate-950 rounded border border-slate-800 mb-1">
                            <span className="text-slate-300">{item.description} ({item.quantity} {item.unit})</span>
                            <span className="text-slate-200">Rs {item.amount.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {cost.labor.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-400 mb-1">Labor</p>
                        {cost.labor.map(item => (
                          <div key={item.item_id} className="flex justify-between text-xs py-1 px-2 bg-slate-950 rounded border border-slate-800 mb-1">
                            <span className="text-slate-300">{item.description} ({item.quantity} {item.unit})</span>
                            <span className="text-slate-200">Rs {item.amount.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {cost.service.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-400 mb-1">Service</p>
                        {cost.service.map(item => (
                          <div key={item.item_id} className="flex justify-between text-xs py-1 px-2 bg-slate-950 rounded border border-slate-800 mb-1">
                            <span className="text-slate-300">{item.description} ({item.quantity} {item.unit})</span>
                            <span className="text-slate-200">Rs {item.amount.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-slate-700 pt-2 mt-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Subtotal</span>
                        <span className="text-slate-200">Rs {cost.subtotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Tax ({cost.tax_rate}%)</span>
                        <span className="text-slate-300">Rs {cost.tax_amount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold pt-1 border-t border-slate-700">
                        <span className="text-slate-200">Total</span>
                        <span className="text-emerald-400">Rs {cost.total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {!cost && (
                <Card title="No Cost Entry" subtitle="Add cost entry to track expenses for this work order">
                  <p className="text-xs text-slate-500 text-center py-4">No cost entry has been recorded for this work order yet.</p>
                </Card>
              )}

              {quotations.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-slate-400">Quotations ({quotations.length})</h4>
                  {quotations.map(q => (
                    <QuotationApprovalView
                      key={q.quotation_id}
                      quotation={q}
                      onApprove={async (id) => {
                        await approveQuotation(id);
                        refreshBilling();
                      }}
                      onReject={async (id, reason) => {
                        await rejectQuotation(id, reason);
                        refreshBilling();
                      }}
                    />
                  ))}
                </div>
              )}

              {bills.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-slate-400">Bills ({bills.length})</h4>
                  {bills.map(b => (
                    <BillCard
                      key={b.bill_id}
                      bill={b}
                      onMarkPaid={async (id) => {
                        await markBillPaid(id);
                        refreshBilling();
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
